import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";

import { delay, map } from "rxjs/operators";
import { AsyncRetriever } from "../interface/async-retriever.interface";
import { Controller } from "../interface/controller.interface";
import { AsyncJobError } from "../model/error/async-job.error";
import { HTTPMethod } from "../model/http-method.enum";
import { HTTPRequestBuilder } from "../model/http.request.model";
import { MimeType } from "../model/mime-type.enum";
import { AsyncJobId } from "../persistence/dto/async-job-id.model";
import { JobState } from "../persistence/dto/job-state.enum";
import { AsyncJobState } from "../persistence/view-dto/async-job-state.model";
import { HTTPService } from "./http.service";

	/**
	 * @description
	 *  Servicio reflejo del controlador de nivel de jobs asincrónicos del backend.
	 *
	 * @see gigot-backend/Core/Controller/AsyncJobController.cs
	 */

@Injectable({ providedIn: "root" })
export class AsyncJobService implements Controller {

	constructor(private httpService: HTTPService) {}

	public basePath(): string {
		return "api/asyncJob";
	}

	/**
	 * @description
	 *	Obtiene el resultado como blob (a.k.a. stream), de un job ejecutado de
	 *	forma asincrónica que fue completado con éxito.
	 *
	 * @param asyncJobId
	 *	El ID del job y de su resultado.
	 * @param mimeType
	 *	El Mime-type del stream a descargar.
	 *
	 * @returns
	 *	Un observable con el resultado, en caso de existir, o un resultado
	 *	vacío de otro modo.
	 */
	public retrieve(asyncJobId: AsyncJobId, mimeType: MimeType): Observable<Blob> {
		return this.httpService.send<typeof undefined, Blob>(
			new HTTPRequestBuilder()
				.method(HTTPMethod.GET)
				.controllerPath(this.basePath())
				.relativePath(`${asyncJobId.jobId}/${asyncJobId.retrievalGuid}`)
				.responseType("blob")
				.build())
			.pipe(map((response) => new Blob([response], { type: mimeType })));
	}

	/**
	 * @description
	 *	Análogo al método "retrieve(...)", pero para deserializar el flujo
	 *	como un objeto JSON. El objeto devuelto requiere reificación, en caso
	 *	de que sea necesario el acceso a sus métodos.
	 */
	public retrieveAsJSON<T>(asyncJobId: AsyncJobId): Observable<T> {
		return this.httpService.send<typeof undefined, T>(
			new HTTPRequestBuilder()
				.method(HTTPMethod.GET)
				.controllerPath(this.basePath())
				.relativePath(`${asyncJobId.jobId}/${asyncJobId.retrievalGuid}.json`)
				.build());
	}

	/**
	 * @description
	 *	Consulta el estado de ejecución de un job asincrónico. Cuando el
	 *	estado del mismo alcance el valor "Succeeded", entonces es posible
	 *	extraer el resultado mediante un llamado a "retrieve" o
	 *	"retreiveAsJSON" según corresponda.
	 */
	public stateOf(asyncJobId: AsyncJobId): Observable<AsyncJobState> {
		return this.httpService.send<typeof undefined, AsyncJobState>(
			new HTTPRequestBuilder()
				.method(HTTPMethod.GET)
				.controllerPath(this.basePath())
				.relativePath(`${asyncJobId.jobId}/${asyncJobId.retrievalGuid}/state`)
				.build())
			.pipe(map(response => AsyncJobState.from(response)));
	}

	/**
	 * @description
	 *	Intenta descargar un stream de bytes (blob) de forma asincrónica con
	 *	una estrategia de polling, donde dicho stream representa el resultado
	 *	de un job.
	 *
	 * @param asyncJobId
	 *	El ID del job asincrónico que genera el stream.
	 * @param asyncRetriever
	 *	El manejador de la consulta y descarga del resultado.
	 */
	public tryToRetrieveBlob(asyncJobId: AsyncJobId, asyncRetriever: AsyncRetriever<Blob>): void {
		const endpointForRetrieve = this.retrieve(asyncJobId, asyncRetriever.mimeType());
		this.tryToRetrieve<Blob>(asyncJobId, asyncRetriever, endpointForRetrieve);
	}

	/**
	 * @description
	 *	Análogo al método "tryToRetrieveBlob(...)", pero para descargar
	 *	objetos JSON. Este objeto puede utilizarse directamente en caso de que
	 *	solo se realicen accesos a propiedades, de otro modo (para acceder a
	 *	métodos del modelo), debería reificarse.
	 *
	 * @param asyncJobId
	 *	El ID del job asincrónico que genera el stream.
	 * @param asyncRetriever
	 *	El manejador de la consulta y descarga del resultado.
	 */
	public tryToRetrieveJSON<T>(asyncJobId: AsyncJobId, asyncRetriever: AsyncRetriever<T>): void {
		const endpointForRetrieve = this.retrieveAsJSON<T>(asyncJobId);
		this.tryToRetrieve<T>(asyncJobId, asyncRetriever, endpointForRetrieve);
	}

	/**
	 * @description
	 *	Intenta obtener el resultado de un job ejecutado de forma asincrónica,
	 *	hasta que el mismo se complete o falle, verificando el estado del
	 *	mismo de forma intermitente cada cierto intervalo de tiempo
	 *	customizable.
	 *
	 * @todo
	 *	Transformar en un Observable para que todo el proceso permanezca
	 *	stand-by hasta que alguien se subscriba al mismo.
	*/
	private tryToRetrieve<T>(asyncJobId: AsyncJobId, asyncRetriever: AsyncRetriever<T>, endpointForRetrieve: Observable<T>): void {
		this.stateOf(asyncJobId)
			.subscribe({
				next: asyncJobState => {
					switch (asyncJobState.jobState) {
						case JobState.Succeeded:
							// Ok, descargar.
							endpointForRetrieve.subscribe({
								next: result => asyncRetriever.consume(result),
								error: (error: Error) => asyncRetriever.error(error)
							});
							break;
						case JobState.Deleted:
						case JobState.Failed:
						case JobState.Unknown:
							// Error, cancelar.
							asyncRetriever.error(new AsyncJobError("Error al obtener un resultado.", {
								asyncJobId, asyncJobState
							}));
							break;
						default:
							// Esperar y reintentar.
							of(asyncJobId)
								.pipe(delay(asyncRetriever.delayBetweenRetries(asyncJobState)))
								.subscribe(asyncJobId => this.tryToRetrieve(asyncJobId, asyncRetriever, endpointForRetrieve));
							break;
					}
				},
				error: (error: Error) => asyncRetriever.error(error)
			});
	}

	public tryToVerifyJobState(asyncJobId: AsyncJobId, throttler: number): Observable<{ jobState: JobState; jobId: string }> {
		return new Observable<{ jobState: JobState; jobId: string }>(jobIdState => {
			this.stateOf(asyncJobId)
				.subscribe({
					next: asyncJobState => {
						const result = { jobState: asyncJobState.jobState, jobId: String(asyncJobId) };
						switch (asyncJobState.jobState) {
							case JobState.Succeeded:
								jobIdState.next(result);
								jobIdState.complete();
								break;
							case JobState.Deleted:
							case JobState.Failed:
							case JobState.Unknown:
								jobIdState.error(new AsyncJobError("Error al obtener un resultado.", {
									asyncJobId,
									asyncJobState
								}));
								break;
							default:
								of(asyncJobId)
									.pipe(delay(throttler))
									.subscribe(asyncJobId => {
										this.tryToVerifyJobState(asyncJobId, throttler)
											.subscribe(
												result => jobIdState.next(result), 
												error => jobIdState.error(error)
											);
									});
								break;
						}
					},
					error: (error: Error) => jobIdState.error(error)
				});
		});
	}
}
